import {
  ArrowLeftRight,
  ChartNoAxesCombined,
  ClipboardCheck,
  Factory,
  Globe,
  KeyRound,
  Landmark,
  Mail,
  MessagesSquare,
  Percent,
  Rss,
  ScrollText,
  ShoppingBag,
  Truck,
  Undo2,
  Warehouse,
  Webhook,
  type LucideIcon,
} from 'lucide-react';

import type { Permission } from '@nucleus-commerce/admin-kit/permissions';

export type TileRoute =
  | '/s/$storeId/settings/markets'
  | '/s/$storeId/settings/payment-methods'
  | '/s/$storeId/settings/shipping'
  | '/s/$storeId/settings/tax'
  | '/s/$storeId/settings/locations'
  | '/s/$storeId/settings/workflows'
  | '/s/$storeId/settings/qc-checklists'
  | '/s/$storeId/settings/returns'
  | '/s/$storeId/settings/recovery'
  | '/s/$storeId/settings/notifications'
  | '/s/$storeId/settings/policies'
  | '/s/$storeId/settings/feeds'
  | '/s/$storeId/settings/analytics'
  | '/s/$storeId/settings/chat'
  | '/s/$storeId/settings/imports-exports'
  | '/s/$storeId/settings/api-keys'
  | '/s/$storeId/settings/webhooks';

export interface Tile {
  key: string;
  icon: LucideIcon;
  to: TileRoute;
  /** The same gate as the target route's `beforeLoad`. */
  permission: Permission | Permission[];
  mode?: 'all' | 'any';
}

export interface TileGroup {
  key: 'selling' | 'orders' | 'onlineStore' | 'developers';
  tiles: Tile[];
}

export const SETTINGS_TILE_GROUPS: TileGroup[] = [
  {
    key: 'selling',
    tiles: [
      {
        key: 'markets',
        icon: Globe,
        to: '/s/$storeId/settings/markets',
        permission: 'settings.read',
      },
      {
        key: 'paymentMethods',
        icon: Landmark,
        to: '/s/$storeId/settings/payment-methods',
        permission: 'payments.manage',
      },
      {
        key: 'shipping',
        icon: Truck,
        to: '/s/$storeId/settings/shipping',
        permission: 'settings.read',
      },
      { key: 'tax', icon: Percent, to: '/s/$storeId/settings/tax', permission: 'settings.read' },
      {
        key: 'locations',
        icon: Warehouse,
        to: '/s/$storeId/settings/locations',
        permission: 'inventory.read',
      },
    ],
  },
  {
    key: 'orders',
    tiles: [
      {
        key: 'workflows',
        icon: Factory,
        to: '/s/$storeId/settings/workflows',
        permission: 'settings.read',
      },
      {
        key: 'qcChecklists',
        icon: ClipboardCheck,
        to: '/s/$storeId/settings/qc-checklists',
        permission: 'settings.read',
      },
      {
        key: 'returns',
        icon: Undo2,
        to: '/s/$storeId/settings/returns',
        permission: 'settings.read',
      },
      {
        key: 'recovery',
        icon: ShoppingBag,
        to: '/s/$storeId/settings/recovery',
        permission: 'settings.read',
      },
      {
        key: 'notifications',
        icon: Mail,
        to: '/s/$storeId/settings/notifications',
        // Marketing holds notifications.write without settings.read; the route accepts either.
        permission: ['notifications.write', 'settings.read'],
        mode: 'any',
      },
    ],
  },
  {
    key: 'onlineStore',
    tiles: [
      {
        key: 'policies',
        icon: ScrollText,
        to: '/s/$storeId/settings/policies',
        permission: 'content.read',
      },
      { key: 'feeds', icon: Rss, to: '/s/$storeId/settings/feeds', permission: 'content.read' },
      {
        key: 'chat',
        icon: MessagesSquare,
        to: '/s/$storeId/settings/chat',
        permission: 'conversations.read',
      },
      {
        key: 'analytics',
        icon: ChartNoAxesCombined,
        to: '/s/$storeId/settings/analytics',
        permission: 'settings.read',
      },
    ],
  },
  {
    key: 'developers',
    tiles: [
      {
        key: 'importsExports',
        icon: ArrowLeftRight,
        to: '/s/$storeId/settings/imports-exports',
        permission: ['exports.run', 'imports.run'],
        mode: 'any',
      },
      {
        key: 'apiKeys',
        icon: KeyRound,
        to: '/s/$storeId/settings/api-keys',
        permission: 'api_keys.manage',
      },
      {
        key: 'webhooks',
        icon: Webhook,
        to: '/s/$storeId/settings/webhooks',
        permission: 'webhooks.manage',
      },
    ],
  },
];

/** True when the role reaches at least one settings screen; the sidebar entry follows this, not `settings.read`. */
export function canOpenStoreSettings(
  can: (permission: Permission | Permission[], mode?: 'all' | 'any') => boolean,
): boolean {
  return SETTINGS_TILE_GROUPS.some((group) =>
    group.tiles.some((tile) => can(tile.permission, tile.mode)),
  );
}
